import type { StandingRow } from '@league/shared/contract';

/**
 * SORTING THE WEEKLY STANDINGS (owner, 2026-10-12) — by any week's score or by the season total,
 * either way. Display only: it reorders rows on the director's screen and changes nothing stored,
 * no rank, and nothing a player sees.
 *
 *   - A player with no score that week — away, or not entered yet — always goes to the bottom,
 *     whichever way the column is sorted.
 *   - Ties go to the higher season total, then alphabetically by name.
 *   - The default, Total highest first, is the table exactly as the API ranks it, so it keeps the
 *     league's own tiebreakers rather than the alphabet.
 */
export type SortKey = 'total' | number;
export interface StandingsSort {
  /** 'total', or the index of a session in `bySession`. */
  key: SortKey;
  dir: 'desc' | 'asc';
}

export const DEFAULT_SORT: StandingsSort = { key: 'total', dir: 'desc' };

/** That week's score, or null when there is none to sort by. */
export function weekValue(row: StandingRow, index: number): number | null {
  if (row.awayBySession?.[index]) return null;
  return row.bySession[index] ?? null;
}

const nameOf = (row: StandingRow) => `${row.player.firstName} ${row.player.lastName}`.trim();

export function sortStandings(rows: StandingRow[], sort: StandingsSort): StandingRow[] {
  if (sort.key === 'total' && sort.dir === 'desc') return rows;
  const value = (row: StandingRow) => (sort.key === 'total' ? row.points : weekValue(row, sort.key));
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === null && vb !== null) return 1;
    if (vb === null && va !== null) return -1;
    if (va !== null && vb !== null && va !== vb) return sort.dir === 'desc' ? vb - va : va - vb;
    if (a.points !== b.points) return b.points - a.points;
    return nameOf(a).localeCompare(nameOf(b));
  });
}

/** Clicking a column: a new column starts highest first; the same one flips. */
export function nextSort(current: StandingsSort, key: SortKey): StandingsSort {
  return current.key === key ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' };
}
